import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertIcon, ArrowDownIcon, ArrowUpIcon, BoltIcon, ChartIcon, FireIcon, Modal, useToast } from "@signet/ui";
import { CreatorAvatar, useMe, type CreatorPhotos } from "@signet/web-kit";
import { BrandMark } from "../../creators/web/brand-marks.tsx";
import { BANDS, change, type BandId } from "../shared/flow.ts";
import { monthLabel, saveAverage, shortMonth, subsKeys, timeLabel, type Flow, type FlowRow, type MonthAverage } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The sub flow board, shared by the Subs page and the panel on Now, laid out
   from the boss's mockup (2026-10-13): one tab per band with how many
   creators are in it, and clicking a band lists only those creators, so 50
   creators never fill the screen at once.
   ────────────────────────────────────────────────────────────────────────── */

const BAND_ICON: Record<BandId, typeof AlertIcon> = { poor: AlertIcon, average: ChartIcon, strong: ArrowUpIcon, popping: BoltIcon };

/**
 * A band, Urgent (the top creators' week, the boss 2026-10-13), or List: under
 * Fansly the four bands are one list and there is no Urgent (Jay, 2026-10-13
 * and 14), since a few Fansly pages split four ways is mostly empty tabs.
 */
export type Tab = BandId | "urgent" | "list";

/** OnlyFans or Fansly; there is no All (Jay, 2026-10-14). */
export type PlatformPick = "onlyfans" | "fansly";

// Best on the left, worst on the right (the boss, 2026-10-13), then Urgent.
const ORDER: BandId[] = ["popping", "strong", "average", "poor"];

const number = (n: number) => n.toLocaleString();

const onPlatform = (rows: FlowRow[], platform: PlatformPick) => rows.filter((r) => r.platform === platform);

/** The rows a tab shows on a platform: one band, or every Fansly page for List. */
export function rowsFor(flow: Flow, tab: Tab, platform: PlatformPick): FlowRow[] {
  const rows = onPlatform(flow.rows, platform);
  return tab === "list" ? rows : rows.filter((r) => r.band === tab);
}

/** The band that opens first: the highest one with anybody in it. */
export function firstBand(flow: Flow | undefined, platform: PlatformPick = "onlyfans"): BandId {
  const rows = flow ? onPlatform(flow.rows, platform) : [];
  for (const band of ORDER) if (rows.some((r) => r.band === band)) return band;
  return "strong";
}

const BAND_KEY = "signet.subs.band";
const PLATFORM_KEY = "signet.subs.platform";

function stored<T extends string>(key: string, ok: (value: string | null) => boolean): T | null {
  try {
    const saved = localStorage.getItem(key);
    return ok(saved) ? (saved as T) : null;
  } catch {
    return null;
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private window or blocked storage: the switch still works, it just isn't remembered.
  }
}

/** OnlyFans or Fansly, remembered in this browser for the page and the panel on Now alike. A remembered "all" opens on OnlyFans. */
export function usePlatform(): [PlatformPick, (platform: PlatformPick) => void] {
  const [platform, setPlatform] = useState<PlatformPick>(() => stored<PlatformPick>(PLATFORM_KEY, (v) => v === "onlyfans" || v === "fansly") ?? "onlyfans");
  return [platform, (next) => (setPlatform(next), store(PLATFORM_KEY, next))];
}

/**
 * The tab picked last in this browser, fitted to the platform: Fansly has only
 * its List, and OnlyFans falls back from List to the first band with creators.
 */
export function useBand(flow: Flow | undefined, platform: PlatformPick): [Tab, (tab: Tab) => void] {
  const [picked, setPicked] = useState<Tab | null>(() => stored<Tab>(BAND_KEY, (v) => v === "urgent" || v === "list" || BANDS.some((b) => b.id === v)));
  const pick = (tab: Tab) => (setPicked(tab), store(BAND_KEY, tab));
  if (platform === "fansly") return ["list", pick];
  if (picked === "urgent") return ["urgent", pick];
  return [picked && picked !== "list" ? picked : firstBand(flow, platform), pick];
}

/** OnlyFans and Fansly across the full width, above the bands, each by its logo. */
export function PlatformSwitch({ value, onChange }: { value: PlatformPick; onChange: (platform: PlatformPick) => void }) {
  const options: { id: PlatformPick; label: string }[] = [
    { id: "onlyfans", label: "OnlyFans" },
    { id: "fansly", label: "Fansly" },
  ];
  return (
    <div className="subs-platforms" role="tablist" aria-label="Platform">
      {options.map((o) => (
        <button key={o.id} type="button" role="tab" aria-selected={value === o.id} className={`subs-platform-tab subs-platform-${o.id}${value === o.id ? " on" : ""}`} onClick={() => onChange(o.id)}>
          <BrandMark platform={o.id} size={15} />
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function BandTabs({ flow, band, onBand, platform }: { flow: Flow | undefined; band: Tab; onBand: (tab: Tab) => void; platform: PlatformPick }) {
  const rows = flow ? onPlatform(flow.rows, platform) : [];
  const fansly = platform === "fansly";
  return (
    <div className={`subs-bands${fansly ? " single" : ""}`} role="tablist" aria-label="Sub flow bands">
      {fansly ? (
        <button type="button" role="tab" aria-selected={band === "list"} className={`subs-band subs-band-list${band === "list" ? " on" : ""}`} onClick={() => onBand("list")}>
          <BrandMark platform="fansly" size={14} />
          <span className="subs-band-name">List</span>
          {/* Pages with only counts in so far are in too. */}
          <span className="subs-band-count tabular">{rows.length + (flow?.countsOnly ?? []).filter((c) => c.platform === "fansly").length}</span>
          <span className="subs-band-range">Every Fansly page</span>
        </button>
      ) : (
        ORDER.map((id) => {
          const b = BANDS.find((x) => x.id === id)!;
          const Icon = BAND_ICON[id];
          const count = rows.filter((r) => r.band === id).length;
          return (
            <button key={id} type="button" role="tab" aria-selected={band === id} className={`subs-band subs-band-${id}${band === id ? " on" : ""}`} onClick={() => onBand(id)}>
              <Icon size={14} />
              <span className="subs-band-name">{b.label}</span>
              <span className="subs-band-count tabular">{count}</span>
              <span className="subs-band-range">{b.range}</span>
            </button>
          );
        })
      )}
      {fansly ? null : (
        <button type="button" role="tab" aria-selected={band === "urgent"} className={`subs-band subs-band-urgent${band === "urgent" ? " on" : ""}`} onClick={() => onBand("urgent")}>
          <FireIcon size={14} />
          <span className="subs-band-name">Urgent</span>
          <span className="subs-band-range">Top creators</span>
        </button>
      )}
    </div>
  );
}

/** "+12%" or "+5", green up or red down; a faint dot with nothing to compare against. */
export function Change({ now, before, title }: { now: number; before: number | null; title?: string }) {
  const c = change(now, before);
  if (!c) return <span className="subs-faint" title="Nothing to compare against">·</span>;
  const up = c.value >= 0;
  const Icon = up ? ArrowUpIcon : ArrowDownIcon;
  return (
    <span className={`subs-change ${c.value === 0 ? "flat" : up ? "up" : "down"} tabular`} title={title}>
      {c.value === 0 ? null : <Icon size={12} />}
      {c.kind === "pct" ? `${Math.abs(c.value)}%` : `${up ? "+" : "-"}${Math.abs(c.value)}`}
    </span>
  );
}

/** A small line of daily totals, gaps left where nothing was entered. */
export function Trend({ values, label }: { values: (number | null)[]; label: string }) {
  const width = 120;
  const height = 26;
  const known = values.filter((v): v is number => v !== null);
  if (known.length < 2) return <span className="subs-trend empty" role="img" aria-label={label} />;
  const min = Math.min(...known);
  const max = Math.max(...known);
  const x = (i: number) => 2 + (i / Math.max(1, values.length - 1)) * (width - 4);
  const y = (v: number) => height - 3 - (max === min ? 0.5 : (v - min) / (max - min)) * (height - 6);
  const points = values.map((v, i) => (v === null ? null : ([x(i), y(v)] as const))).filter((p): p is readonly [number, number] => p !== null);
  const line = points.map(([px, py], i) => `${i ? "L" : "M"}${px.toFixed(1)},${py.toFixed(1)}`).join(" ");
  const area = `${line} L${points.at(-1)![0].toFixed(1)},${height} L${points[0]![0].toFixed(1)},${height} Z`;
  return (
    <span className="subs-trend" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <path d={area} className="subs-trend-area" />
        <path d={line} className="subs-trend-line" />
      </svg>
    </span>
  );
}

const cell = (value: number | null) => (value === null ? <span className="subs-faint">-</span> : number(value));

/** Their average day last month, and under it the pages that make it up ("300 free · 150 VIP"). */
function MonthAvg({ avg }: { avg: MonthAverage | null }) {
  if (!avg) return <span className="subs-faint" title="Nothing entered that month">-</span>;
  if (avg.byHand) {
    return (
      <span className="subs-avg" title="Set by hand for that month, in place of the days entered">
        <b className="tabular">{number(avg.total)}</b>
        <small>set by hand</small>
      </span>
    );
  }
  const parts = [avg.free !== null ? `${number(avg.free)} free` : null, avg.vip !== null ? `${number(avg.vip)} VIP` : null, avg.third !== null ? `${number(avg.third)} third` : null].filter(Boolean);
  return (
    <span className="subs-avg" title={`An average over the ${avg.days} ${avg.days === 1 ? "day" : "days"} entered that month`}>
      <b className="tabular">{number(avg.total)}</b>
      {parts.length > 1 ? <small>{parts.join(" · ")}</small> : null}
    </span>
  );
}

/**
 * The average cell, which whoever can edit numbers can click to set the month's average by hand
 * (Ben, 2026-11-02: an OFTV feature put ABBY's October at 3,699 a day, so every day after read as a crash).
 */
function AvgCell({ row, month }: { row: FlowRow; month: string }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  if (!can("subs.entry:edit")) return <MonthAvg avg={row.monthAvg} />;
  return (
    <>
      <button type="button" className="subs-avg-button" title={`Set ${row.creator}'s ${monthLabel(month)} average by hand`} onClick={() => setOpen(true)}>
        <MonthAvg avg={row.monthAvg} />
      </button>
      {open ? <AverageModal row={row} month={month} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function AverageModal({ row, month, onClose }: { row: FlowRow; month: string; onClose: () => void }) {
  const toast = useToast();
  const client = useQueryClient();
  const [value, setValue] = useState(row.monthAvg?.byHand ? String(row.monthAvg.total) : "");
  const who = `${row.creator}${row.platform === "fansly" ? " Fansly" : ""}`;
  const save = useMutation({
    mutationFn: (total: number | null) => saveAverage(row.id, month, total),
    onSuccess: (_, total) => {
      void client.invalidateQueries({ queryKey: subsKeys.all });
      toast.success("Saved", total === null ? `${who}'s ${monthLabel(month)} average is worked out from the days again.` : `${who}'s ${monthLabel(month)} average is now ${number(total)} a day.`);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't save", error.message),
  });
  const parsed = value.trim() === "" ? null : Number(value);
  const ok = parsed !== null && Number.isInteger(parsed) && parsed >= 0;
  return (
    <Modal
      open
      size="confirm"
      title={`${who}'s ${monthLabel(month)} average`}
      description="What each day is compared against. The days themselves stay as entered."
      onClose={onClose}
      footer={
        <>
          {row.monthAvg?.byHand ? (
            <button type="button" className="text-button" disabled={save.isPending} onClick={() => save.mutate(null)}>
              Use the days again
            </button>
          ) : null}
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!ok || save.isPending} onClick={() => ok && save.mutate(parsed)}>
            Save
          </button>
        </>
      }
    >
      <label className="subs-avg-field">
        <span>Subs a day</span>
        <input autoFocus inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => (e.key === "Enter" && ok ? save.mutate(parsed) : undefined)} />
      </label>
      {!row.monthAvg?.byHand && row.monthAvg ? <p className="subs-faint">Worked out from the days: {number(row.monthAvg.total)} a day.</p> : null}
    </Modal>
  );
}

/** One band's creators, highest total first. */
export function FlowTable({ flow, rows, photos, oneDay }: { flow: Flow; rows: FlowRow[]; photos: CreatorPhotos; oneDay: boolean }) {
  return (
    <div className="subs-table-wrap">
      <table className="subs-table subs-flow-table">
        {/* Fixed widths so the seven number columns share the space evenly; with
            auto widths each one sized to its own text and the gaps went uneven (Ben, 2026-11-02). */}
        <colgroup>
          <col className="subs-col-rank" />
          <col className="subs-col-who" />
          {Array.from({ length: 7 }, (_, i) => <col key={i} />)}
          <col className="subs-col-trend" />
        </colgroup>
        <thead>
          <tr>
            <th className="subs-rank">#</th>
            <th className="subs-who">Creator</th>
            <th>Total</th>
            {/* The month's daily average sits beside Total, the figure it is read against (Ben, 2026-10-29). */}
            <th>{shortMonth(flow.avgMonth)} daily avg</th>
            <th>vs {shortMonth(flow.avgMonth)} avg</th>
            <th>Free</th>
            <th>VIP</th>
            <th>Third page</th>
            <th>{oneDay ? "vs day before" : "vs before"}</th>
            <th className="subs-trend-head">{oneDay ? "Trend (7 days)" : "Trend"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td className="subs-rank tabular">{i + 1}</td>
              <td className="subs-who">
                <span>
                  <CreatorAvatar name={r.creator} src={photos(r.creator)} size={26} />
                  <strong>{r.creator}</strong>
                  {r.platform === "fansly" ? <span className="subs-platform">Fansly</span> : null}
                </span>
              </td>
              <td className="subs-total tabular">{number(r.total)}</td>
              <td>
                <AvgCell row={r} month={flow.avgMonth} />
              </td>
              <td>
                {/* A day against the month's average day; a longer window by its own average day. */}
                <Change now={oneDay ? r.total : Math.round(r.total / r.days)} before={r.monthAvg?.total ?? null} />
              </td>
              <td className="tabular">{cell(r.free)}</td>
              <td className="tabular">{cell(r.vip)}</td>
              <td className="tabular">{cell(r.third)}</td>
              <td>
                <Change now={r.total} before={r.previous} title={r.previous === null ? undefined : `${oneDay ? "The day before" : "The period before"}: ${number(r.previous)}`} />
              </td>
              <td className="subs-trend-cell">
                <Trend values={r.spark} label={`${r.creator}: ${r.spark.map((v) => (v === null ? "none" : v)).join(", ")}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length ? <p className="subs-none">{flow.noData ? "Nothing entered yet." : "Nobody in this band."}</p> : null}
    </div>
  );
}

/** "1,284 total subs · ↑12% vs the day before · Avg. 25.2 per account · 51/51 reported · Updated 1:08pm" */
export function Summary({ flow, versus }: { flow: Flow; versus: string }) {
  const average = flow.rows.length ? flow.totals.total / flow.rows.length : 0;
  return (
    <span className="subs-summary">
      <span>
        <b className="tabular">{number(flow.totals.total)}</b> total subs
      </span>
      {flow.previousTotal !== null ? (
        <span>
          <Change now={flow.totals.total} before={flow.previousTotal} /> {versus}
        </span>
      ) : null}
      {flow.rows.length ? <span>Avg. {average.toLocaleString(undefined, { maximumFractionDigits: 1 })} subs/account</span> : null}
      <span className="tabular">
        {flow.reported}/{flow.accounts} accounts reported
      </span>
      {flow.updatedAt ? <span>Updated {timeLabel(flow.updatedAt)}</span> : null}
    </span>
  );
}

/**
 * Fansly pages whose counts are in but whose growth starts the next day: there
 * are no counts the day before to subtract from. Listed with their photo and
 * counts so they read as in, not missing (Jay, 2026-10-14).
 */
export function CountsOnly({ flow, photos }: { flow: Flow; photos: CreatorPhotos }) {
  const pages = flow.countsOnly ?? [];
  if (!pages.length) return null;
  return (
    <div className="subs-counts-only">
      <p>
        <b>Counts in, growth from the next day ({pages.length}).</b> Growth is today's counts less the day before's, and these have none from the day before yet.
      </p>
      <ul>
        {pages.map((p) => (
          <li key={p.id}>
            <CreatorAvatar name={p.creator} src={photos(p.creator)} size={22} />
            <strong>{p.creator}</strong>
            <span className="subs-faint tabular">
              {p.freeTotal === null ? "-" : number(p.freeTotal)} followers · {p.vipTotal === null ? "-" : number(p.vipTotal)} subscribers
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
